/**
 * usePWA Hook
 *
 * Manages PWA service worker registration and updates
 */

/// <reference types="vite-plugin-pwa/react" />
import { useRegisterSW } from 'virtual:pwa-register/react'
import { useCallback, useEffect } from 'react'

interface PWAStatus {
  /** Whether a new version of the app is available */
  needRefresh: boolean
  /** Whether the app is ready for offline use */
  offlineReady: boolean
  /** Function to trigger the service worker update */
  updateServiceWorker: () => void
}

/**
 * Hook to manage PWA service worker and updates
 *
 * @returns PWAStatus object with update state and trigger function
 */
export function usePWA(): PWAStatus {
  const {
    needRefresh: [needRefresh],
    offlineReady: [offlineReady],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(registration: ServiceWorkerRegistration | undefined) {
      console.log('[PWA] Service worker registered:', registration)

      // Check for updates every hour
      if (registration) {
        setInterval(
          () => {
            registration.update()
          },
          60 * 60 * 1000
        )
      }
    },
    onRegisterError(error: Error) {
      console.error('[PWA] Service worker registration failed:', error)
    },
    onNeedRefresh() {
      console.log('[PWA] New content available, please refresh')
    },
    onOfflineReady() {
      console.log('[PWA] App is ready for offline use')
    },
  })

  const handleUpdate = useCallback(() => {
    updateServiceWorker(true)
  }, [updateServiceWorker])

  // Log state changes for debugging
  useEffect(() => {
    if (needRefresh) {
      console.log('[PWA] Update available - waiting for user action')
    }
  }, [needRefresh])

  useEffect(() => {
    if (offlineReady) {
      console.log('[PWA] Offline ready')
    }
  }, [offlineReady])

  return {
    needRefresh,
    offlineReady,
    updateServiceWorker: handleUpdate,
  }
}
